import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';

import { axiosBaseQuery } from '@/redux/axios';
import {
  CreateFaultAlarmConfigurationPayload,
  FaultAlarmConfigurationItem,
  FaultAlarmResult,
  UpdateFaultAlarmConfigurationPayload,
} from '@/apps/health_check/types/IHealthCheck';
import {
  createNotificationHandler,
  SCOPES,
  MESSAGES,
} from '@/redux/notificationHandler';
import { apiRoutes } from '@/api/apiRoutes';
import { GetFaultAlarmConfigurationsParams } from '@/api/types';

const faultAlarmConfigurationsApi = createApi({
  reducerPath: 'faultAlarmConfigurationsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['FaultAlarmConfigurations'],
  endpoints: (builder) => ({
    getFaultAlarmConfigurations: builder.query<
      FaultAlarmResult,
      GetFaultAlarmConfigurationsParams
    >({
      query: (params) => ({
        url: apiRoutes.getFaultAlarmConfigurations.path(params),
        method: apiRoutes.getFaultAlarmConfigurations.method,
      }),
      providesTags: ['FaultAlarmConfigurations'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
        MESSAGES.LOAD,
        false
      ),
    }),
    updateFaultAlarmConfiguration: builder.mutation<
      FaultAlarmConfigurationItem,
      { id: number | string } & UpdateFaultAlarmConfigurationPayload
    >({
      query: ({ id, ...patch }) => ({
        url: apiRoutes.updateFaultAlarmConfigurationById.path(id),
        method: apiRoutes.updateFaultAlarmConfigurationById.method,
        data: patch,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
          MESSAGES.UPDATE
        )({ id, ...patch }, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;

        dispatch(
          faultAlarmConfigurationsApi.util.updateQueryData(
            'getFaultAlarmConfigurations',
            { contractId: updatedItem.contractId },
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft[idx] = updatedItem;
              }
            }
          )
        );
      },
    }),
    createFaultAlarmConfiguration: builder.mutation<
      FaultAlarmConfigurationItem,
      CreateFaultAlarmConfigurationPayload
    >({
      query: (patch) => ({
        url: apiRoutes.createFaultAlarmConfiguration.path(),
        method: apiRoutes.createFaultAlarmConfiguration.method,
        data: patch,
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
          MESSAGES.ADD
        )(data, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;

        dispatch(
          faultAlarmConfigurationsApi.util.updateQueryData(
            'getFaultAlarmConfigurations',
            { contractId: updatedItem.contractId },
            (draft) => {
              draft.push(updatedItem);
            }
          )
        );
      },
    }),
    deleteFaultAlarmConfiguration: builder.mutation<
      { success: boolean },
      { id: string | number; contractId?: number }
    >({
      query: ({ id }) => ({
        url: apiRoutes.deleteFaultAlarmConfigurationById.path(id),
        method: apiRoutes.deleteFaultAlarmConfigurationById.method,
      }),
      async onQueryStarted({ id, contractId }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
          MESSAGES.DELETE
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;

        dispatch(
          faultAlarmConfigurationsApi.util.updateQueryData(
            'getFaultAlarmConfigurations',
            { contractId },
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetFaultAlarmConfigurationsQuery,
  useCreateFaultAlarmConfigurationMutation,
  useDeleteFaultAlarmConfigurationMutation,
  useUpdateFaultAlarmConfigurationMutation,
} = faultAlarmConfigurationsApi;

export default faultAlarmConfigurationsApi;
